import { NavigateFunction } from 'react-router'

import Cookies from 'universal-cookie'

import { ProviderWebKey } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { ProviderUser } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { setLdInitialized } from './appSlice'
import { AxiosInstance, baseApi, RTKQueryTag } from './baseApi'
import * as mixpanelTracker from '../../../mixpanel/mixpanelTracking'
import { oktaBaseUrl } from '../common/constants/appConstants'
import {
  actionAlertHandler,
  actionStyles,
  LOGIN,
  LOGOUT,
  SAVE_LAST_AUTHORIZED_ROUTE,
} from '../common/constants/reduxConstants'
import { LOGIN as LOGIN_ROUTE } from '../common/constants/routingConstants'
import { RoleConfig } from '../common/roleConfigs/types'
import { identifyLaunchDarklyWithTimeout } from '../common/utils/launchDarklyIdentification'
import { isEngageDAProvider, isEngageICASProvider } from '../common/utils/utils'
import { clearZendeskIdentity } from '../common/utils/zendeskMessaging'

import type { LDClient } from 'launchdarkly-js-client-sdk'

type LoginArgs = {
  queryString: string
  navigate: NavigateFunction
  ldClient?: LDClient
}

type LoginResponse = {
  access_token?: string
  user?: ProviderUser
  da_migration_completed_during_login?: boolean
}

type LogoutArgs = {
  clearLastPage?: boolean
}

type WebKeyParams = {
  providerId: string
}

const cookies = new Cookies()

const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginArgs>({
      query: ({ queryString }) => ({
        method: 'get',
        url: `v1/okta/callback${queryString}`,
        withCredentials: true,
        axiosInstanceType: AxiosInstance.GENERIC,
        showAlert: false,
      }),
      async onQueryStarted({ navigate, ldClient }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const configOverrides: RoleConfig = {}
          if (data.access_token && data.user) {
            const { id: userId, roles: userRoles } = data.user
            const isDAEngageProvider = !!userRoles && isEngageDAProvider(userRoles)
            const isICASEngageProvider = !!userRoles && isEngageICASProvider(userRoles)
            if (ldClient && (isDAEngageProvider || isICASEngageProvider)) {
              try {
                await identifyLaunchDarklyWithTimeout(() =>
                  ldClient.identify({
                    kind: 'user',
                    key: userId,
                    roles: userRoles,
                  }),
                )
                // Use the login result if migration just ran, before the LD update reaches this client.
                // Both the login result and the DA-named flag also apply to ICAS Engage providers.
                configOverrides.bcPlatform =
                  data.da_migration_completed_during_login === true || ldClient.variation('isDAProviderMigrated', true)
                // Enable video sessions for DA providers when flag is enabled
                if (isDAEngageProvider) {
                  configOverrides.videoSessionsEnabled = ldClient.variation(
                    'isDAEngageOnOffPlatformVideoEnabled',
                    false,
                  )
                }
              } catch (error) {
                console.warn('LaunchDarkly identify failed:', error)
              } finally {
                dispatch(setLdInitialized())
              }
            }

            mixpanelTracker.setUser(data.user)
            mixpanelTracker.track({ event: 'LOGIN' })

            dispatch({
              type: LOGIN,
              payload: { ...data, configOverrides },
            })
          } else {
            actionAlertHandler({
              actionStyle: actionStyles.ERROR,
              message: 'There was an error in retrieving an access token. Please try again.',
              dispatch,
            })
          }
        } catch (error) {
          navigate(LOGIN_ROUTE.route)
          actionAlertHandler({
            actionStyle: actionStyles.ERROR,
            message: error.error,
            dispatch,
          })
        }
      },
    }),
    logout: builder.mutation<void, LogoutArgs>({
      query: () => ({
        method: 'delete',
        url: `${oktaBaseUrl}/api/v1/sessions/me`,
        withCredentials: true,
        axiosInstanceType: AxiosInstance.GENERIC,
        showAlert: false,
      }),
      onQueryStarted: ({ clearLastPage = false }, { dispatch, queryFulfilled }) => {
        queryFulfilled.finally(() => {
          clearZendeskIdentity()
          cookies.remove('session', { path: '/' })
          sessionStorage.clear()
          if (clearLastPage) {
            dispatch({
              type: SAVE_LAST_AUTHORIZED_ROUTE,
              payload: '',
            })
          }
          mixpanelTracker.track({ event: 'LOGOUT' })
          dispatch({
            type: LOGOUT,
          })
        })
      },
    }),
    getWebKey: builder.query<ProviderWebKey, WebKeyParams>({
      query: ({ providerId }) => ({
        method: 'get',
        url: `v1/providers/${providerId}/web-key`,
        showAlert: false,
      }),
      providesTags: [RTKQueryTag.WEB_KEY],
    }),
    createWebKey: builder.mutation<ProviderWebKey, WebKeyParams>({
      query: ({ providerId }) => ({
        method: 'post',
        url: `v1/providers/${providerId}/web-key`,
        showAlert: false,
      }),
      invalidatesTags: [RTKQueryTag.WEB_KEY],
    }),
  }),
})

export const { useLoginMutation, useLogoutMutation, useGetWebKeyQuery, useCreateWebKeyMutation } = authApi
export const logoutMutation = authApi.endpoints.logout
